// src/utils/productUtils.ts
import { Product, Category, Warehouse } from '../enums/product';

export const generateSKU = (name: string, category: string): string => {
  const nameCode = name.substring(0, 3).toUpperCase();
  const categoryCode = category.substring(0, 2).toUpperCase();
  const timestamp = Date.now().toString().slice(-4);
  return `${nameCode}-${categoryCode}-${timestamp}`;
};

export const formatPrice = (price: number, currency: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(price);
};

export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload JPEG, PNG, WebP, or GIF images only.',
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File size too large. Please upload images smaller than 10MB.',
    };
  }

  return { valid: true };
};

export const compressImage = (file: File, maxWidth: number = 1200, quality: number = 0.8): Promise<File> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;

      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            resolve(file);
          }
        },
        file.type,
        quality
      );
    };

    img.src = URL.createObjectURL(file);
  });
};

export const sortProducts = (
  products: Product[], 
  sortBy: string, 
  sortOrder: 'asc' | 'desc' = 'asc',
  categories: Category[] = []
): Product[] => {
  return [...products].sort((a, b) => {
    let aValue: any = a[sortBy as keyof Product];
    let bValue: any = b[sortBy as keyof Product];

    // Special handling for category sorting - use category name instead of ID
    if (sortBy === 'category' && categories.length > 0) {
      const aCategory = categories.find(cat => cat.id === a.categoryId);
      const bCategory = categories.find(cat => cat.id === b.categoryId);
      aValue = aCategory ? aCategory.name : a.categoryId;
      bValue = bCategory ? bCategory.name : b.categoryId;
    }

    // Handle date objects
    if (aValue instanceof Date) aValue = aValue.getTime();
    if (bValue instanceof Date) bValue = bValue.getTime();

    // Handle strings
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
};

export const filterProducts = (
  products: Product[], 
  filters: {
    search?: string;
    category?: string;
    warehouse?: string; // Add warehouse filter
    inStock?: boolean;
    outOfStock?: boolean;
    withVariants?: boolean;
    withoutVariants?: boolean;
  },
  categories: Category[] = []
): Product[] => {
  return products.filter((product) => {
    // Search filter
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const productCategory = categories.find(cat => cat.id === product.categoryId);
      const categoryName = productCategory ? productCategory.name.toLowerCase() : '';
      
      const searchMatch = 
        product.name.toLowerCase().includes(search) ||
        product.description.toLowerCase().includes(search) ||
        product.sku.toLowerCase().includes(search) ||
        product.categoryId.toLowerCase().includes(search) ||
        categoryName.includes(search);
      
      if (!searchMatch) return false;
    }

    // Category filter
    if (filters.category && product.categoryId !== filters.category) {
      return false;
    }

    // Availability filters — CarStatus is the single source of truth
    if (filters.inStock && product.status !== 'In Stock') return false;
    if (filters.outOfStock && product.status === 'In Stock') return false;

    return true;
  });
};

export const getProductCategories = (products: Product[], categories: Category[]): string[] => {
  const categoryIds = new Set(products.map(product => product.categoryId));
  const categoryNames = Array.from(categoryIds).map(categoryId => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  });
  return categoryNames.sort();
};

export const getProductCategoryOptions = (products: Product[], categories: Category[]): Array<{id: string, name: string}> => {
  const categoryIds = new Set(products.map(product => product.categoryId));
  return Array.from(categoryIds).map(categoryId => {
    const category = categories.find(cat => cat.id === categoryId);
    return {
      id: categoryId,
      name: category ? category.name : categoryId
    };
  }).sort((a, b) => a.name.localeCompare(b.name));
};

export const getCategoryName = (categoryId: string, categories: Category[]): string => {
  const category = categories.find(cat => cat.id === categoryId);
  return category ? category.name : categoryId;
};

export const getCategoryId = (categoryName: string, categories: Category[]): string | null => {
  const category = categories.find(cat => cat.name === categoryName);
  return category ? category.id! : null;
};

// Several brand-new units of the same brand+model+trim+color are functionally
// interchangeable (unlike pre-owned cars, which always differ in mileage/
// condition/year). This counts how many other rows in the currently-loaded
// product list match `product` on that basis, so the Cars table can flag
// "N identical" without collapsing or hiding any row — staff still see and
// manage every VIN individually. Best-effort: only counts within whatever
// page/list is currently loaded, not the whole catalog.
export const getIdenticalNewUnitCount = (products: Product[], product: Product): number => {
  if (product.condition !== 'Brand New' || !product.brandId || !product.modelId) return 1;
  return products.filter(p =>
    p.condition === 'Brand New' &&
    p.brandId === product.brandId &&
    p.modelId === product.modelId &&
    (p.trimId || '') === (product.trimId || '') &&
    (p.exteriorColor || '') === (product.exteriorColor || '')
  ).length;
};

// Helper function to get warehouse name from ID
export const getWarehouseName = (warehouseId: string, warehouses: Warehouse[]): string => {
  if (!warehouseId) return 'N/A';
  const warehouse = warehouses.find(w => w.id === warehouseId);
  return warehouse ? `${warehouse.name} (${warehouse.code})` : warehouseId;
};

export const getProductStats = (products: Product[], categories: Category[] = []) => {
  const total = products.length;
  const inStock = products.filter(p => p.status === 'In Stock').length;
  const outOfStock = products.filter(p => p.status !== 'In Stock').length;

  // Get unique category names instead of IDs
  const uniqueCategories = new Set(
    products.map(product => {
      const category = categories.find(cat => cat.id === product.categoryId);
      return category ? category.name : product.categoryId;
    })
  );
  
  const categoryCount = uniqueCategories.size;
  const totalValue = products.reduce((sum, p) => sum + p.price, 0);

  return {
    total,
    inStock,
    outOfStock,
    categories: categoryCount,
    totalValue,
    averagePrice: total > 0 ? totalValue / total : 0,
  };
};

export const getProductsWithCategoryNames = (products: Product[], categories: Category[]): Array<Product & { categoryName: string }> => {
  return products.map(product => {
    const category = categories.find(cat => cat.id === product.categoryId);
    return {
      ...product,
      categoryName: category ? category.name : product.categoryId
    };
  });
};

export const groupProductsByCategory = (products: Product[], categories: Category[]): Record<string, Product[]> => {
  const grouped: Record<string, Product[]> = {};
  
  products.forEach(product => {
    const category = categories.find(cat => cat.id === product.categoryId);
    const categoryName = category ? category.name : product.categoryId;
    
    if (!grouped[categoryName]) {
      grouped[categoryName] = [];
    }
    
    grouped[categoryName].push(product);
  });
  
  return grouped;
};

export const getCategoryStats = (products: Product[], categories: Category[]) => {
  const grouped = groupProductsByCategory(products, categories);
  
  return Object.entries(grouped).map(([categoryName, categoryProducts]) => {
    const inStock = categoryProducts.filter(p => p.status === 'In Stock').length;
    const outOfStock = categoryProducts.filter(p => p.status !== 'In Stock').length;
    const totalValue = categoryProducts.reduce((sum, p) => sum + p.price, 0);
    
    return {
      categoryName,
      productCount: categoryProducts.length,
      inStock,
      outOfStock,
      totalValue,
      averagePrice: categoryProducts.length > 0 ? totalValue / categoryProducts.length : 0
    };
  }).sort((a, b) => b.productCount - a.productCount);
};

  // Helper function to get currency symbol
  export function getCurrencySymbol(currencyCode: string): string {
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      JPY: '¥',
      CAD: 'C$',
      AUD: 'A$',
      CNY: '¥',
      INR: '₹',
      BRL: 'R$',
      MXN: '$',
      SGD: 'S$',
      HKD: 'HK$',
      CHF: 'CHF',
      KRW: '₩',
      RUB: '₽',
      ZAR: 'R',
      NZD: 'NZ$',
      SEK: 'kr',
      NOK: 'kr',
      DKK: 'kr',
      PLN: 'zł',
      TRY: '₺',
      THB: '฿',
      IDR: 'Rp',
      MYR: 'RM',
      PHP: '₱',
      VND: '₫',
    };
    return symbols[currencyCode] || currencyCode;
  }